import { Button, Stack, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Copy, TickCircle } from "iconsax-react";
import { memo, useCallback, useEffect, useState } from "react";

import type { CopyLinkRowProps } from "./CopyLinkRow.types";
import * as styles from "./CopyLinkRow.styles";

export type { CopyLinkRowProps } from "./CopyLinkRow.types";

const COPIED_FEEDBACK_MS = 2000;

export const CopyLinkRow = memo(function CopyLinkRow({
  url,
  size = "lg",
  onCopy,
  copyLabel = "کپی لینک",
  copiedLabel = "کپی شد",
}: CopyLinkRowProps) {
  const theme = useTheme();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;
    const timer = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [isCopied]);

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(url).then(() => setIsCopied(true));
    onCopy?.(url);
  }, [onCopy, url]);

  const iconSize = size === "sm" ? 16 : 18;
  const iconColor = theme.palette.primary.contrastText;

  return (
    <Stack direction="row" sx={styles.root}>
      <TextField
        value={url}
        onFocus={(event) => event.target.select()}
        slotProps={{ input: { readOnly: true }, htmlInput: { dir: "ltr" } }}
        sx={styles.input(size)}
      />
      <Button
        variant="contained"
        color={isCopied ? "success" : "primary"}
        fullWidth={false}
        onClick={handleCopy}
        startIcon={
          isCopied ? (
            <TickCircle size={iconSize} variant="Bold" color={iconColor} />
          ) : (
            <Copy size={iconSize} variant="Linear" color={iconColor} />
          )
        }
        sx={styles.copyButton(size)}
      >
        {isCopied ? copiedLabel : copyLabel}
      </Button>
    </Stack>
  );
});
